import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  IconButton,
  InputAdornment,
  Tooltip
} from '@mui/material';
import { Visibility, VisibilityOff, InfoOutlined } from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';

const Login = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [registrationConfig, setRegistrationConfig] = useState(null);

  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Get the intended destination or default to dashboard
  const from = location.state?.from?.pathname || '/';

  // Fetch registration configuration on mount
  useEffect(() => {
    const fetchRegistrationConfig = async () => {
      try {
        const response = await api.get('/config/registration');
        setRegistrationConfig(response.data);
      } catch (err) {
        console.error('Failed to fetch registration config:', err);
        // Default to allowing registration if fetch fails
        setRegistrationConfig({
          enabled: true,
          redirect_to_waitlist: false,
          waitlist_message: ''
        });
      }
    };
    
    fetchRegistrationConfig();
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    // Clear error when user starts typing
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.email.trim() || !formData.password.trim()) {
      setError('Please enter both email and password');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const result = await login(formData.email, formData.password);
      
      if (result.success) {
        // Redirect to intended destination or dashboard
        navigate(from, { replace: true });
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container component="main" maxWidth="sm">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Paper elevation={3} sx={{ padding: 4, width: '100%' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Typography component="h1" variant="h4" gutterBottom>
              Sign In
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Access your examr account
            </Typography>

            {error && (
              <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
                {error}
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1, width: '100%' }}>
              <TextField
                margin="normal"
                required
                fullWidth
                id="email"
                label="Email Address"
                name="email"
                autoComplete="email"
                autoFocus
                value={formData.email}
                onChange={handleChange}
                disabled={loading}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                name="password"
                label="Password"
                type={showPassword ? 'text' : 'password'}
                id="password"
                autoComplete="current-password"
                value={formData.password}
                onChange={handleChange}
                disabled={loading}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowPassword(!showPassword)}
                        onMouseDown={(e) => e.preventDefault()}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2 }}
                disabled={loading}
              >
                {loading ? (
                  <CircularProgress size={24} />
                ) : (
                  'Sign In'
                )}
              </Button>
              
              <Box sx={{ textAlign: 'center', mb: 2 }}>
                <Typography variant="body2">
                  <Link
                    to="/forgot-password"
                    style={{
                      color: 'inherit',
                      textDecoration: 'none',
                      fontWeight: 'bold'
                    }}
                  >
                    Forgot your password?
                  </Link>
                </Typography>
              </Box>
              
              <Box sx={{ textAlign: 'center', mb: 2 }}>
                <Typography variant="body2">
                  <Link
                    to="/resend-verification"
                    style={{
                      color: 'inherit',
                      textDecoration: 'none',
                      fontWeight: 'bold'
                    }}
                  >
                    Need to verify your email?
                  </Link>
                </Typography>
              </Box>
              
              {registrationConfig && (
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="body2">
                    Don't have an account?{' '}
                    {registrationConfig.enabled ? (
                      <Link
                        to="/register"
                        style={{
                          color: 'inherit',
                          textDecoration: 'none',
                          fontWeight: 'bold'
                        }}
                      >
                        Sign up here
                      </Link>
                    ) : (
                      <Link
                        to="/"
                        onClick={(e) => {
                          e.preventDefault();
                          // Navigate to landing page and scroll to waitlist
                          navigate('/');
                          setTimeout(() => {
                            const waitlistSection = document.getElementById('waitlist-section');
                            if (waitlistSection) {
                              waitlistSection.scrollIntoView({ behavior: 'smooth' });
                            }
                          }, 100);
                        }}
                        style={{
                          color: 'inherit',
                          textDecoration: 'none',
                          fontWeight: 'bold'
                        }}
                      >
                        {registrationConfig.waitlist_message || 'Join our waitlist'}
                      </Link>
                    )}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default Login;